/**
 * Onboarding AI page: chat with Oracle AI using chatChannel "member_onboarding".
 * Complete when final event has success: true (or client signal)
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import retry from 'retry-as-promised'
import { useSelector, useDispatch } from 'react-redux'
import { Map } from 'immutable'
import { usePostHogEvent } from '../../PostHog/index'
import { selectAuth } from '../../../services/auth/selectors'
import { selectOnboardingGenericTerms } from '../../../services/onboarding/selectors'
import { getOnboardingStatus } from '../../../services/onboarding/actions'
import { sendOptimizelyFeatureEvent } from '../../../services/optimizely-feature/actions'
import { setEventDefault, setAiGa4Event } from '../../../services/event-tracking/actions'
import {
  ONBOARDING_COMPLETED_GA4_EVENT,
  ONBOARDING_SKIPPED_GA4_EVENT,
  ONBOARDING_CHAT_ENDED_GA4_EVENT,
  AI_MESSAGE_SENT_GA4_EVENT,
} from '../../../services/event-tracking/index'
import Button, { TYPES } from '../../Button/index'
import { ChatInput } from '../../OracleCommons/ChatInput'
import { OracleLoadingSpinner } from '../../OracleCommons/index'
import { OracleAgentMessage } from '../../OraclePage/OracleAgentMessage'
import { OracleUserMessage } from '../../OraclePage/OracleUserMessage'
import { H1 } from '../../Heading/index'
import GaiaLogo, { TYPE_TEAL } from '../../GaiaLogo/GaiaLogo'
import ArrowRight from '../../Icon/icons/ArrowRight/index'
import {
  StaticTextProvider,
  useTranslatedText,
} from '../../OracleCommons/StaticTextContext'
import type { InternationalStrings } from '../../OracleCommons/StaticTextContext'
import OptimizelyFeature from '../../OptimizelyFeature/index'
import { useOnboardingChatSubscription } from './hooks'
import { stripIncompleteGaiaTag, replaceGaiaTagsInMarkdown } from './utils'

interface CompletedExchange {
  question: string
  response: string
}

const ONBOARDING_STATUS_CHECK = {
  IDLE: 'idle',
  CHECKING: 'checking',
  CONFIRMED: 'confirmed',
  FAILED: 'failed',
} as const

type OnboardingStatusCheckState =
  typeof ONBOARDING_STATUS_CHECK[keyof typeof ONBOARDING_STATUS_CHECK]

// Same default personality as GotS (guide); can be made configurable later.
const DEFAULT_PERSONALITY_ID = '2'

export interface OnboardingAIProps {
  setPage: (forward?: boolean) => void
  setTopics: (term: unknown) => void
  /** When provided, Skip button goes to Topics screen instead of advancing with generic term */
  onSkipToTopics?: () => void
  /** When user clicks the button in complete state; navigate to home. */
  onGoHome?: () => void
  /** When chat errors, Continue advances and runs v5 onboarding flow. */
  onContinueToFinal?: (genericTerm: unknown) => void
  staticText: InternationalStrings
  language: string
  hasContentImpression?: boolean
  onContinueToPlaylist?: (option?: unknown | null, force?: boolean) => void
}

function OnboardingAI (props: OnboardingAIProps) {
  const { staticText } = props
  // The Redux selector delivers the flat translation map (state.staticText.data.onboarding.data).
  // useTranslatedText prepends ['data', ...keys], so wrap it to match the expected shape.
  const wrappedStaticText = useMemo(() => Map({ data: staticText }), [staticText])
  return (
    <StaticTextProvider staticText={wrappedStaticText as InternationalStrings}>
      <OnboardingAIInner {...props} />
    </StaticTextProvider>
  )
}

function OnboardingAIInner (props: OnboardingAIProps) {
  const {
    setPage,
    setTopics,
    onSkipToTopics,
    onGoHome,
    onContinueToFinal,
    language,
    hasContentImpression,
    onContinueToPlaylist,
  } = props

  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const jwt = (auth.get('jwt') as string) || ''
  const genericTerm = useSelector(selectOnboardingGenericTerms).first()

  const [chatSessionId, setChatSessionId] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [completedExchanges, setCompletedExchanges] = useState<CompletedExchange[]>([])
  const [onboardingCompleteFromSignal, setOnboardingCompleteFromSignal] = useState(false)
  const [statusCheckState, setStatusCheckState] = useState<OnboardingStatusCheckState>(
    ONBOARDING_STATUS_CHECK.IDLE,
  )
  const actionAreaRef = useRef<HTMLDivElement>(null)
  const conversationEventFiredRef = useRef(false)

  const trackConversationStarted = usePostHogEvent('ai_conversation_started')
  const trackMessageSent = usePostHogEvent('ai_message_sent')
  const trackMessageReceived = usePostHogEvent('ai_message_received')

  // Translated strings
  const skipText = useTranslatedText({ keys: ['skip'] })
  const whatBroughtYouHereText = useTranslatedText({ keys: ['whatBroughtYouHere'] })
  const introAIParagraphText = useTranslatedText({ keys: ['introAIParagraph'] })
  const placeholderText = useTranslatedText({ keys: ['placeholder'] })
  const disclaimerText = useTranslatedText({ keys: ['disclaimer'] })
  const completingSetupText = useTranslatedText({ keys: ['completingSetup'] })
  const goHomeText = useTranslatedText({ keys: ['goHome'] })
  const continueText = useTranslatedText({ keys: ['continue'] })
  const endChatText = useTranslatedText({ keys: ['endChat'] })
  const wrapUpText = useTranslatedText({ keys: ['wrapUp'] })
  const lookingForSpecificShowText = useTranslatedText({ keys: ['lookingForSpecificShow'] })
  const seekingAnswersToBigQuestionsText = useTranslatedText({ keys: ['seekingAnswersToBigQuestions'] })
  const exploringMyPathText = useTranslatedText({ keys: ['exploringMyPath'] })

  const onMessageComplete = useCallback((data: {
    messagePairId: string
    chatSessionId: string
    sessionTitle?: string | null
    userMessage: string
    oracleMessage: string
  }) => {
    trackMessageReceived(data.messagePairId, data.oracleMessage.length, 'Text')
    setCompletedExchanges((prev) => [
      ...prev,
      { question: data.userMessage, response: data.oracleMessage },
    ])
  }, [trackMessageReceived])

  const handleClientSignals = useCallback((signals: Array<{ type: string; payload?: unknown }>) => {
    const payloadSuccess = (s: { type: string; payload?: unknown }) => (
      (s.payload as { success?: boolean } | undefined)?.success === true
    )
    const success = signals.some((s) => s.type === 'member_onboarding' && payloadSuccess(s))

    if (success) {
      setOnboardingCompleteFromSignal(true)
      setStatusCheckState(ONBOARDING_STATUS_CHECK.CHECKING)
    }
  }, [])

  const {
    streamingText,
    isStreaming,
    pendingMessage,
    sendMessage,
    error: subscriptionError,
  } = useOnboardingChatSubscription({
    personalityId: DEFAULT_PERSONALITY_ID,
    jwt,
    chatSessionId,
    onSessionReceived: useCallback((sessionId: string) => {
      setChatSessionId(sessionId)
    }, []),
    onError: useCallback((err: Error) => {
      setErrorMessage(err.message)
    }, []),
    onMessageComplete,
    onClientSignals: handleClientSignals,
    language,
  })

  const handleSendMessage = useCallback((message: string, uiElement: string = 'Text Field') => {
    const inputMethod = uiElement === 'Easy Select' ? 'Prompt' as const : 'Text' as const
    if (!conversationEventFiredRef.current) {
      conversationEventFiredRef.current = true
      trackConversationStarted(inputMethod, 'Onboarding')
    }

    trackMessageSent(message, inputMethod, uiElement)

    setErrorMessage(null)
    sendMessage(message)
    dispatch(setAiGa4Event(AI_MESSAGE_SENT_GA4_EVENT.mergeDeep({
      ui_element: uiElement,
      ui_location: 'AI onboarding',
      input_method: 'text entered',
      ai_persona: DEFAULT_PERSONALITY_ID,
    })))
  }, [sendMessage, dispatch, trackConversationStarted, trackMessageSent])

  const handleEndChatAndMoveOn = useCallback(() => {
    const wrapUpPhrase = wrapUpText || 'Wrap this up'
    handleSendMessage(wrapUpPhrase)
    dispatch(setEventDefault(ONBOARDING_CHAT_ENDED_GA4_EVENT))
  }, [handleSendMessage, wrapUpText, dispatch])

  const handleGoHomeClick = useCallback(() => {
    dispatch(sendOptimizelyFeatureEvent({ eventName: 'onboarding_completed' }))
    dispatch(setEventDefault(ONBOARDING_COMPLETED_GA4_EVENT.mergeDeep({
      ui_location: 'AI onboarding',
    })))
    onGoHome?.()
  }, [dispatch, onGoHome])

  const handleFallbackContinue = useCallback(() => {
    dispatch(sendOptimizelyFeatureEvent({ eventName: 'onboarding_completed' }))
    onContinueToFinal?.(genericTerm)
  }, [dispatch, genericTerm, onContinueToFinal])

  const handleSkipClick = useCallback(() => {
    const canGoHome = (statusCheckState === ONBOARDING_STATUS_CHECK.CONFIRMED
      || (onboardingCompleteFromSignal && statusCheckState !== ONBOARDING_STATUS_CHECK.FAILED))
      && onGoHome
    if (canGoHome) {
      onGoHome?.()
      dispatch(sendOptimizelyFeatureEvent({ eventName: 'onboarding_completed' }))
      dispatch(setEventDefault(ONBOARDING_COMPLETED_GA4_EVENT.mergeDeep({
        ui_location: 'AI onboarding',
      })))
    } else if (onSkipToTopics) {
      onSkipToTopics()
      dispatch(setEventDefault(ONBOARDING_SKIPPED_GA4_EVENT.mergeDeep({
        ui_location: 'AI onboarding',
      })))
    } else {
      setTopics(genericTerm)
      setPage()
    }
  }, [
    statusCheckState,
    onboardingCompleteFromSignal,
    onGoHome,
    onSkipToTopics,
    genericTerm,
    setTopics,
    setPage,
  ])

  // On completion signal, poll getOnboardingStatus with retry until backend has marked complete
  useEffect(() => {
    if (statusCheckState !== ONBOARDING_STATUS_CHECK.CHECKING || !auth) {
      return () => {}
    }

    let cancelled = false

    retry(
      async () => {
        // getOnboardingStatus is a thunk; dispatch returns its Promise
        const completed = await (dispatch as (arg: unknown) => Promise<boolean>)(
          getOnboardingStatus({ auth }),
        )
        if (cancelled) throw new Error('cancelled')
        if (!completed) {
          throw new Error('Onboarding not yet complete')
        }
      },
      {
        max: 10,
        backoffBase: 500,
        backoffExponent: 1.2,
        timeout: 15000,
      },
    )
      .then(() => {
        if (!cancelled) setStatusCheckState(ONBOARDING_STATUS_CHECK.CONFIRMED)
      })
      .catch(() => {
        if (!cancelled) setStatusCheckState(ONBOARDING_STATUS_CHECK.FAILED)
      })

    return () => {
      cancelled = true
    }
  }, [statusCheckState, auth, dispatch])

  // Scroll so the action area (Continue or ChatInput) stays in view as content streams
  useEffect(() => {
    if (!isStreaming) return undefined
    const el = actionAreaRef.current
    if (!el) return undefined
    const id = requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'auto', block: 'end' })
    })
    return () => {
      cancelAnimationFrame(id)
    }
  }, [isStreaming, streamingText])

  // Hide incomplete Gaia tags during streaming, then convert complete tags to markdown links
  const markdownForDisplay = useMemo(() => {
    if (!streamingText) return ''
    const withoutIncomplete = stripIncompleteGaiaTag(streamingText)
    return replaceGaiaTagsInMarkdown(withoutIncomplete)
  }, [streamingText])

  const hasError = Boolean(errorMessage || subscriptionError)
  const chatDisabled = isStreaming || onboardingCompleteFromSignal
  const showEndChatAndMoveOn = completedExchanges.length > 0
  const showGoHome = statusCheckState === ONBOARDING_STATUS_CHECK.CONFIRMED && onGoHome
  const showFallbackFromStatusCheck = statusCheckState === ONBOARDING_STATUS_CHECK.FAILED
    && onContinueToFinal

  return (
    <div className="onboarding-ai">
      <div className={`onboarding__launch ${language}`}>
        <div className="onboarding__launch__container">
          <div className="onboarding__gradient-bar" />
          <div className="onboarding__launch-gradient">
            <div className="onboarding-ai__launch-content">
              <div className="onboarding__logo-container">
                <GaiaLogo
                  type={TYPE_TEAL}
                  className={['onboarding__logo']}
                />
                <Button
                  type={TYPES.LINK}
                  onClick={handleSkipClick}
                  className="onboarding-ai__skip-button"
                >
                  {skipText}
                  <ArrowRight />
                </Button>
              </div>
              {!pendingMessage && !isStreaming && completedExchanges.length === 0 && (
                <div className="onboarding__intro-text-wrapper">
                  <H1 className="onboarding-ai__intro-title">
                    { whatBroughtYouHereText }
                  </H1>
                  <p className="onboarding-ai__intro-text">{introAIParagraphText}</p>
                </div>
              )}
              <div className="onboarding-ai__conversation">
                {completedExchanges.map(({ question, response }, idx) => (
                  <React.Fragment key={`exchange-${idx}`}>
                    <OracleUserMessage text={question} />
                    <div className="onboarding-ai__response">
                      <OracleAgentMessage text={replaceGaiaTagsInMarkdown(response)} />
                    </div>
                  </React.Fragment>
                ))}
                {pendingMessage && isStreaming && (
                  <>
                    <OracleUserMessage text={pendingMessage} />
                    <div className="onboarding-ai__response">
                      {!streamingText ? (
                        <OracleLoadingSpinner />
                      ) : (
                        <OracleAgentMessage text={markdownForDisplay} />
                      )}
                    </div>
                  </>
                )}
              </div>
              <div ref={actionAreaRef} className="onboarding-ai__action-area">
                {statusCheckState === ONBOARDING_STATUS_CHECK.CHECKING && (
                  <div className="onboarding-ai__status-checking">
                    <OracleLoadingSpinner />
                    <p className="onboarding-ai__status-checking-text">
                      {completingSetupText}
                    </p>
                  </div>
                )}
                {showGoHome && (
                  <div className="onboarding-ai__continue">
                    {hasContentImpression ? (
                      <>
                        <OptimizelyFeature
                          flag="onboarding_add_to_playlist"
                          variation="control"
                          isDefault={true}
                          anon={false}
                          noWrapper
                        >
                          <Button
                            type={TYPES.PRIMARY}
                            onClick={handleGoHomeClick}
                            className="onboarding-ai__continue-button"
                          >
                            {goHomeText}
                          </Button>
                        </OptimizelyFeature>
                        <OptimizelyFeature
                          flag="onboarding_add_to_playlist"
                          variation="show_add_to_playlist"
                          noWrapper
                        >
                          <Button
                            type={TYPES.PRIMARY}
                            onClick={() => onContinueToPlaylist(null, true)}
                            className="onboarding-ai__continue-button"
                          >
                            {continueText}
                          </Button>
                        </OptimizelyFeature>
                      </>
                    ) : (
                      <Button
                        type={TYPES.PRIMARY}
                        onClick={handleGoHomeClick}
                        className="onboarding-ai__continue-button"
                      >
                        {goHomeText}
                      </Button>
                    )}
                  </div>
                )}
                {showFallbackFromStatusCheck && (
                  <div className="onboarding-ai__fallback">
                    <Button
                      type={TYPES.PRIMARY}
                      onClick={handleFallbackContinue}
                      className="onboarding-ai__continue-button"
                    >
                      {continueText}
                    </Button>
                  </div>
                )}
                {hasError && onContinueToFinal && !showFallbackFromStatusCheck && (
                  <div className="onboarding-ai__fallback">
                    <p className="onboarding-ai__error" role="alert">
                      {errorMessage || subscriptionError?.message}
                    </p>
                    <Button
                      type={TYPES.PRIMARY}
                      onClick={handleFallbackContinue}
                      className="onboarding-ai__continue-button"
                    >
                      {continueText}
                    </Button>
                  </div>
                )}
                {hasError && !onContinueToFinal && (
                  <div className="onboarding-ai__error" role="alert">
                    {errorMessage || subscriptionError?.message}
                  </div>
                )}
                <div className="onboarding-ai__chat">
                {!onboardingCompleteFromSignal && !hasError
                  && statusCheckState === ONBOARDING_STATUS_CHECK.IDLE && (
                  <div className="onboarding-ai__chat-input">
                    <ChatInput
                      onSendMessage={handleSendMessage}
                      disabled={chatDisabled}
                      placeholder={placeholderText}
                      isStreaming={isStreaming}
                    />
                    <p className="onboarding-ai__disclaimer">
                      {disclaimerText}
                    </p>
                    {showEndChatAndMoveOn && (
                      <div className="onboarding-ai__end-chat">
                        <Button
                          type={TYPES.PRIMARY}
                          onClick={handleEndChatAndMoveOn}
                          disabled={chatDisabled}
                          className="onboarding-ai__end-chat-button"
                        >
                          {endChatText}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {!pendingMessage && !isStreaming && completedExchanges.length === 0 && (
                  <div className="onboarding-ai__footer">
                    <Button
                      type={TYPES.LINK}
                      className="onboarding-ai__easy-select"
                      onClick={() => handleSendMessage(lookingForSpecificShowText, 'Easy Select')}
                    >
                      {lookingForSpecificShowText}
                      <ArrowRight />
                    </Button>
                    <Button
                      type={TYPES.LINK}
                      className="onboarding-ai__easy-select"
                      onClick={() => handleSendMessage(seekingAnswersToBigQuestionsText, 'Easy Select')}
                    >
                      {seekingAnswersToBigQuestionsText}
                      <ArrowRight />
                    </Button>
                    <Button
                      type={TYPES.LINK}
                      className="onboarding-ai__easy-select"
                      onClick={() => handleSendMessage(exploringMyPathText, 'Easy Select')}
                    >
                      {exploringMyPathText}
                      <ArrowRight />
                    </Button>
                  </div>
                )}
                </div>
              </div>
              <div className="onboarding-ai__bottom-buttons">
                <Button
                  type={TYPES.LINK}
                  onClick={handleSkipClick}
                  className="onboarding-ai__skip-button"
                >
                  {skipText}
                  <ArrowRight />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnboardingAI
